
import { merge, Observable, merge as observableMerge, ReplaySubject, Subscription } from 'rxjs';

import { Inject, Injectable, OnDestroy } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import * as _ from "lodash";
import { filter, map, tap } from 'rxjs/operators';
import { WebShortcuts } from "../../../app.services/lookups/lookups.types/interfaces/web.shortcuts";
import { TranslateService } from "../../translate";
import { WINDOW_LISTENER_TOKEN, WindowEvent, WindowHostListenerService } from "../../window/window.host.listener.service";
import { ActionShortcut } from "../interfaces/action.shortcut";
import { IHotKey } from "../interfaces/Ihotkey";
import { IHotKeyEvent } from "../interfaces/Ihotkey.event";
import { numberKeyCodes, numPadNumbersKeyCodes } from "../keyboard.map";
import { HotKeyUtils } from "./hotkey.utils";
import { ShortcutsConfigService } from "./shortcuts.config.service";
import WebShortcut = WebShortcuts.WebShortcut;

@UntilDestroy()
@Injectable({ providedIn: 'root' })
  export class HotKeysService implements OnDestroy {

	static hotKeysEnabled = true;

	public SEPARATOR = "+";

	public keydownObservable: Observable<KeyboardEvent>;

	public pressKeys: number[] = [];

	public hotKeys: { [code: string]: IHotKey };
	private hotKeysSubj: ReplaySubject<{ [code: string]: IHotKey }> = new ReplaySubject<{[p: string]: IHotKey}>(1);

	private loadShortcutsSubscription: Subscription;

	constructor(@Inject(WINDOW_LISTENER_TOKEN) private windowHostListenerService: WindowHostListenerService,
				private shortcutsConfigService: ShortcutsConfigService,
				private hotKeyUtils: HotKeyUtils,
				private translateService:TranslateService,) {
		this.loadShortcutsSubscription = this.shortcutsConfigService.loadShortcuts()
			.pipe(untilDestroyed(this))
			.subscribe((shortcutsData) => {
				this.buildShortcutsData(shortcutsData.webShortcutsDS.webShortcut, shortcutsData.actionsShortcut);
			});

		this.keydownObservable = this.windowHostListenerService.getObservable<KeyboardEvent>(WindowEvent.keydown).pipe(//getObservableForActiveElement
			untilDestroyed(this),
			tap((event) => this.addPressKey((event).keyCode)));

		this.windowHostListenerService.getObservable<KeyboardEvent>(WindowEvent.keyup)//getObservableForActiveElement
			.pipe(untilDestroyed(this))
			.subscribe((event) => this.removePressKey((event).keyCode));

		merge(this.windowHostListenerService.getOffObservable(),//getOffObservableForActiveElement
			this.windowHostListenerService.getObservable(WindowEvent.blur))
			.pipe(untilDestroyed(this))
			.subscribe(() => this.pressKeys = []);
	}

	public ngOnDestroy(): void {
		this.loadShortcutsSubscription?.unsubscribe();
	}

	public loadHotKeys(): Observable<{ [code: string]: IHotKey }> {
		return this.hotKeysSubj.asObservable();
	}

	public addHotKey(hotKey: IHotKey) {
		this.hotKeys[hotKey.code] = hotKey;
	}

	public getHotKey(code: string): IHotKey {
		return this.hotKeys ? this.hotKeys[code] : null;
	}

	public isHotKey(code: string): boolean {
		return !!(this.hotKeys && this.hotKeys[code]);
	}

	public getObservable(codes: string | string[], preventDefault: boolean = true): Observable<IHotKeyEvent> {
		if (codes) {
			let codesArr = _.isArray(codes) ? codes : [codes];

			let hotKeysObservables = (<string[]>codesArr).reduce((observables, code) => {
				if (this.hotKeys && this.hotKeys[code]) {
					observables.push(this.keydownObservable.pipe(
						filter((event) => this.isPressed(code)),
						tap((event) => preventDefault ? event.preventDefault() : null),
						tap((event) => event.stopPropagation()),
						map((event) => Object.assign({}, this.hotKeys[code], {'event': event})),)
					);
				}

				return observables;
			}, []);

			return observableMerge(...hotKeysObservables);
		}
	}

	public enableHotKeys() {
		HotKeysService.hotKeysEnabled = true;
	}

	public disableHotKeys() {
		HotKeysService.hotKeysEnabled = false;
	}


	private buildShortcutsData(webShortcuts: WebShortcut[], actionShortcuts: ActionShortcut[]) {
		this.hotKeys = actionShortcuts.reduce((obj, shortcut) => {
			let hotKey = this.getItemShortcut(shortcut);
			if (hotKey) {
				let code = 'Action.' + shortcut.code.replace(/\s/g, '');
				if (!obj[code]) {
					obj[code] = {code: code, hotKey: hotKey};
				}
			}
			return obj;
		},  webShortcuts.reduce((obj, shortcut) => {
			if (shortcut.code) {
				let hotKey = this.getItemShortcut(shortcut);
				if (hotKey) {
					let code = this.getItemCode(shortcut);
					if (!obj[code]) {
						obj[code] = {code: code, hotKey: hotKey};
					}
				}
			}
			return obj;
		}, {}));

		this.hotKeysSubj.next(this.hotKeys);
	}

	private getItemShortcut(item) {
		if (item.siteShortcut) {
			if (item.siteShortcut === 'None') {
				return "";
			}
			return item.siteShortcut;
		} else {
			return item.defaultShortcut;
		}
	}

	private getItemCode(item) {
		if (item.category === this.hotKeyUtils.GLOBAL_ACTIONS_CATEGORY_ID ||
			item.category === this.hotKeyUtils.WL_ACTIONS_CATEGORY_ID ||
			item.category === this.hotKeyUtils.COMMON_SESSION_ACTIONS_CATEGORY_ID) {
			return item.code.replace(/\s/g, '');
		} else {
			return item.category.replace(/\s/g, '') + '.' +
				item.code.replace(/\s/g, '');
		}
	}

	private isPressed(code: string): boolean {
		if (!HotKeysService.hotKeysEnabled) {
			return false;
		}

		let keyCodes = this.splitHotKey(code);

		if (keyCodes.length === this.pressKeys.length) {
			let keyNotPress = keyCodes.find((keyCode) => {
				return  !this.pressKeys.includes(+keyCode);
			});
			return !keyNotPress;
		}
	}

	private splitHotKey(code: string): string[] {
		return (this.hotKeys && this.hotKeys[code]) ? this.hotKeys[code].hotKey.split(this.SEPARATOR) : [];
	}

	private addPressKey(keyCode: number) {
		if (numPadNumbersKeyCodes.includes(keyCode)) {
			keyCode = numberKeyCodes[keyCode - 96];
		}

		if (!this.pressKeys.length || !this.pressKeys.includes(keyCode)) {
			this.pressKeys.push(keyCode);
		}
	}

	private removePressKey(keyCode: number) {
		let indexOfKeyCode = this.pressKeys.indexOf(keyCode);
		if (indexOfKeyCode > -1) {
			this.pressKeys.splice(indexOfKeyCode, 1);
		}
	}
}
